import { useEffect, useState } from 'react';

import { SearchField } from '@playbooks/molecules/forms';
import { Switch } from '@playbooks/ui/components/switch';

import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
} from 'components/composer/composer-actions-components';
import { useStore, useWorkspace } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody } from 'utils/flow';

const ComposerActionsConnectors = ({ type, selected = [], onChange, onPrev }) => {
	const [query, setQuery] = useState('');
	const [connectors, setConnectors] = useState([]);
	const store = useStore();
	const workspace = useWorkspace();
	const signedIn = Boolean(workspace.workspace?.id && workspace.rootApi);

	const [fetchConnectors, loading, error] = useInit(async () => {
		if (!signedIn) return;
		const response = await store.query({ url: `${workspace.rootApi}/connector-options` });
		setConnectors(response.data || []);
	});

	useEffect(() => {
		if (signedIn) fetchConnectors();
	}, [signedIn, store, workspace.rootApi]);

	const visible = connectors.filter(item =>
		`${item.name || ''} ${item.provider || ''} ${item.providerAccountName || ''}`
			.toLowerCase()
			.includes(query.toLowerCase()),
	);
	const onToggle = connector => {
		if (type !== 'app') return;
		const exists = selected.some(item => item.workspaceConnectorId === connector.id);
		const values = exists
			? selected.filter(item => item.workspaceConnectorId !== connector.id)
			: [...selected, { workspaceConnectorId: connector.id }];
		onChange?.(values.map((item, position) => ({ ...item, position })));
	};

	// Render
	return (
		<>
			<ComposerActionHeader onPrev={onPrev}>
				<SearchField
					value={query}
					placeholder='Search connectors...'
					className='h-8 grow'
					onChange={event => setQuery(event.target.value)}
					onClear={() => setQuery('')}
				/>
			</ComposerActionHeader>
			<FlowBody>
				{!signedIn ? (
					<ComposerActionFeedback
						icon='plug'
						title='Sign in to select connectors'
						text='Workspace connectors become available after sign in.'
					/>
				) : loading ? (
					<ComposerActionListSkeleton count={4} />
				) : error ? (
					<ComposerActionFeedback
						icon='triangle-exclamation'
						title='Connectors unavailable'
						text={error?.message || "We couldn't load Workspace connectors."}
					/>
				) : visible.length ? (
					<div className='grid gap-0.5 p-1'>
						{visible.map(connector => {
							const checked = selected.some(item => item.workspaceConnectorId === connector.id);
							return (
								<ComposerActionRow
									key={connector.id}
									label={connector.name}
									value={connector.providerAccountName || connector.provider}
									selected={checked}
									disabled={type !== 'app'}
									trailing={
										type === 'app' ? <Switch className='pointer-events-none' size='sm' checked={checked} /> : null
									}
									onClick={() => onToggle(connector)}
								/>
							);
						})}
					</div>
				) : (
					<ComposerActionFeedback
						icon='plug'
						title={query ? 'No connectors found' : 'No connected accounts'}
						text={
							query
								? 'Try a different search.'
								: 'Connect a provider in Workspace settings before adding it to a project.'
						}
						className='min-h-48'
					/>
				)}
			</FlowBody>
		</>
	);
};
export { ComposerActionsConnectors };
